import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import {
  Users, GraduationCap, BookOpen, Megaphone, Upload, Trash2, RefreshCw, Plus,
  Search, ShieldCheck, ShieldAlert, Move, BarChart3, Building2, Copy, KeyRound,
} from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "teachers", label: "Teachers", icon: GraduationCap },
  { id: "students", label: "Students", icon: Users },
  { id: "halaqas", label: "Halaqas", icon: BookOpen },
  { id: "announcements", label: "Announcements", icon: Megaphone },
  { id: "settings", label: "Settings & Billing", icon: Building2 },
];

/* ============================================================== Overview */
function OverviewTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/org-admin/overview").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load overview"));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-muted-foreground" data-testid="org-overview-loading">Loading…</div>;
  const tiles = [
    { label: "Students", value: data.students, tone: "forest" },
    { label: "Approved teachers", value: data.teachers_approved, tone: "forest" },
    { label: "Pending teachers", value: data.teachers_pending, tone: "terracotta" },
    { label: "Halaqas", value: data.halaqas, tone: "gold" },
    { label: "Submissions awaiting grade", value: data.submissions_pending, tone: "terracotta" },
    { label: "Active announcements", value: data.active_announcements, tone: "gold" },
  ];
  const tones = { forest: "text-forest-700", gold: "text-gold-600", terracotta: "text-terracotta-600" };
  return (
    <div className="grid grid-cols-2 gap-4" data-testid="org-overview-grid">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-2xl border border-sand-300 bg-white/70 p-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{t.label}</div>
          <div className={`mt-2 text-4xl font-display font-semibold ${tones[t.tone]}`}>{t.value}</div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================== Teachers */
function TeachersTab() {
  const [filter, setFilter] = useState("pending");
  const [list, setList] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setList(null);
    api.get(`/org-admin/teachers?status=${filter}`).then((r) => { if (!cancelled) setList(r.data); }).catch(() => { if (!cancelled) toast.error("Couldn't load teachers"); });
    return () => { cancelled = true; };
  }, [filter]);
  const reload = () => api.get(`/org-admin/teachers?status=${filter}`).then((r) => setList(r.data)).catch(() => {});

  const approve = async (uid) => {
    try { await api.post(`/org-admin/teachers/${uid}/approve`); toast.success("Teacher approved"); reload(); }
    catch { toast.error("Couldn't approve"); }
  };
  const revoke = async (uid) => {
    if (!window.confirm("Revoke this teacher's approval? They will keep their halaqas but cannot grade until re-approved.")) return;
    try { await api.post(`/org-admin/teachers/${uid}/revoke`); toast.success("Approval revoked"); reload(); }
    catch { toast.error("Couldn't revoke"); }
  };

  return (
    <div className="space-y-4" data-testid="org-teachers-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1 p-1 rounded-full bg-sand-200 border border-sand-300" data-testid="org-teacher-filter">
          {["pending", "approved"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`org-teacher-filter-${f}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition capitalize ${
                filter === f ? "bg-forest-600 text-sand-100" : "text-foreground/70 hover:text-foreground"
              }`}>
              {f}
            </button>
          ))}
        </div>
        <button onClick={reload} className="btn-accent text-sm" data-testid="org-teachers-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {list === null ? <div className="text-muted-foreground text-sm">Loading…</div> : list.length === 0 ? (
        <div className="hifz-card text-center py-12 text-muted-foreground" data-testid="org-teachers-empty">
          {filter === "pending" ? "No teachers waiting for approval." : "No approved teachers yet."}
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((t) => (
            <li key={t.user_id} className="hifz-card flex items-center gap-4" data-testid={`org-teacher-row-${t.user_id}`}>
              <div className="h-10 w-10 rounded-full bg-forest-700 text-sand-100 grid place-items-center font-display font-semibold">
                {(t.name || "?")[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-forest-700">{t.name}</div>
                <div className="text-xs text-muted-foreground">{t.email} · {t.classes_count} halaqas</div>
              </div>
              {filter === "pending" ? (
                <button onClick={() => approve(t.user_id)} className="btn-primary text-sm" data-testid={`org-approve-${t.user_id}`}>
                  <ShieldCheck className="h-4 w-4" /> Approve
                </button>
              ) : (
                <button onClick={() => revoke(t.user_id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm border border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10" data-testid={`org-revoke-${t.user_id}`}>
                  <ShieldAlert className="h-4 w-4" /> Revoke
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================== Halaqas */
function HalaqasTab() {
  const [list, setList] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", description: "", schedule: "", teacher_id: "", zoom_url: "", zoom_passcode: "", recurrence: { days_of_week: [], time: "18:00", tz: "Asia/Kuala_Lumpur" } };
  const [form, setForm] = useState(empty);

  const load = () => {
    api.get("/org-admin/halaqas").then((r) => setList(r.data)).catch(() => toast.error("Couldn't load halaqas"));
    api.get("/org-admin/teachers?status=approved").then((r) => setTeachers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openEdit = (cls) => {
    setEditing(cls);
    setForm({
      name: cls.name || "", description: cls.description || "", schedule: cls.schedule || "",
      teacher_id: cls.teacher_id || "", zoom_url: cls.zoom_url || "", zoom_passcode: cls.zoom_passcode || "",
      recurrence: cls.recurrence || { days_of_week: [], time: "18:00", tz: "Asia/Kuala_Lumpur" },
    });
  };
  const openCreate = () => { setEditing({ new: true }); setForm(empty); };
  const close = () => { setEditing(null); setForm(empty); };

  const toggleDay = (d) => {
    setForm((f) => {
      const cur = f.recurrence?.days_of_week || [];
      const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d];
      return { ...f, recurrence: { ...f.recurrence, days_of_week: next } };
    });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      ...form,
      teacher_id: form.teacher_id || null,
      zoom_url: form.zoom_url || null,
      zoom_passcode: form.zoom_passcode || null,
      recurrence: form.recurrence?.days_of_week?.length ? form.recurrence : null,
    };
    try {
      // Org-scoped create/edit/delete reuse the shared /classes endpoints — an
      // org_admin is permitted there and the backend stamps/validates org_id.
      if (editing?.new) await api.post("/classes", payload);
      else await api.patch(`/classes/${editing.class_id}`, payload);
      toast.success("Saved");
      close(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
  };

  const del = async (cls) => {
    if (!window.confirm(`Delete halaqa "${cls.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/classes/${cls.class_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-4" data-testid="org-halaqas-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-muted-foreground">Create, edit, and delete your institution's halaqas.</div>
        <button onClick={openCreate} className="btn-primary text-sm" data-testid="org-halaqas-create-btn">
          <Plus className="h-4 w-4" /> New halaqa
        </button>
      </div>

      {list === null ? <div className="text-muted-foreground text-sm">Loading…</div> : list.length === 0 ? (
        <div className="hifz-card text-center py-12 text-muted-foreground">No halaqas yet.</div>
      ) : (
        <ul className="space-y-2">
          {list.map((c) => (
            <li key={c.class_id} className="hifz-card flex items-center gap-4 flex-wrap" data-testid={`org-halaqa-row-${c.class_id}`}>
              <div className="flex-1 min-w-[240px]">
                <div className="font-display font-semibold text-forest-700">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {c.teacher_name || "No teacher"} · {c.student_count} students · {c.pending_submissions} pending grades
                  {c.zoom_url && <> · <span className="text-forest-700">Zoom set</span></>}
                </div>
                {c.schedule && <div className="text-xs text-muted-foreground mt-0.5">{c.schedule}</div>}
              </div>
              <button onClick={() => openEdit(c)} className="btn-accent text-sm" data-testid={`org-halaqa-edit-${c.class_id}`}>Edit</button>
              <button onClick={() => del(c)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm border border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-500/10" data-testid={`org-halaqa-delete-${c.class_id}`}>
                <Trash2 className="h-4 w-4" /> Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm grid place-items-center p-4" onClick={close} data-testid="org-halaqa-edit-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl border border-sand-300 max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <h3 className="font-display text-xl text-forest-700">{editing?.new ? "New halaqa" : `Edit · ${editing.name}`}</h3>
            <input className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Halaqa name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-halaqa-form-name" />
            <textarea className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="org-halaqa-form-desc" />
            <input className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Human-readable schedule (e.g. Mon, Wed, Fri · 6:00 PM)" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} data-testid="org-halaqa-form-schedule" />
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">Teacher</div>
              <select className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" value={form.teacher_id || ""} onChange={(e) => setForm({ ...form, teacher_id: e.target.value })} data-testid="org-halaqa-form-teacher">
                <option value="">— Assign later —</option>
                {teachers.map((t) => <option key={t.user_id} value={t.user_id}>{`${t.name} (${t.email})`}</option>)}
              </select>
            </div>
            <div className="grid gap-3">
              <input className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Zoom URL" value={form.zoom_url} onChange={(e) => setForm({ ...form, zoom_url: e.target.value })} data-testid="org-halaqa-form-zoom" />
              <input className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Zoom passcode" value={form.zoom_passcode} onChange={(e) => setForm({ ...form, zoom_passcode: e.target.value })} data-testid="org-halaqa-form-passcode" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1.5">Recurrence — days of week</div>
              <div className="flex flex-wrap gap-1.5" data-testid="org-halaqa-form-days">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <button key={d} type="button" onClick={() => toggleDay(d)}
                    className={`px-3 py-1.5 rounded-full text-xs border ${form.recurrence?.days_of_week?.includes(d) ? "bg-forest-600 text-sand-100 border-forest-600" : "bg-white border-sand-300 text-foreground/70"}`}>
                    {d}
                  </button>
                ))}
              </div>
              <div className="mt-2 grid gap-3">
                <input type="time" className="rounded-xl border border-sand-300 px-3 py-2 text-sm" value={form.recurrence?.time || "18:00"} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, time: e.target.value } })} data-testid="org-halaqa-form-time" />
                <input className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Timezone (e.g. Asia/Kuala_Lumpur)" value={form.recurrence?.tz || ""} onChange={(e) => setForm({ ...form, recurrence: { ...form.recurrence, tz: e.target.value } })} data-testid="org-halaqa-form-tz" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={close} className="px-4 py-2 rounded-xl border border-sand-300 text-sm">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="org-halaqa-form-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================== Students */
function StudentsTab() {
  const [students, setStudents] = useState(null);
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [importClass, setImportClass] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [studentEnrollments, setStudentEnrollments] = useState(null);
  const [progressForm, setProgressForm] = useState({ surah: "", ayah: "", status: "memorized" });

  const load = async () => {
    setStudents(null);
    try {
      const [s, c] = await Promise.all([
        api.get("/org-admin/students"),
        api.get("/org-admin/halaqas"),
      ]);
      setStudents(s.data); setClasses(c.data);
    } catch { toast.error("Couldn't load"); }
  };
  useEffect(() => { load(); }, []);

  const activeId = active?.user_id;
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    Promise.all(classes.map((c) =>
      api.get(`/classes/${c.class_id}/students`).then((r) =>
        r.data.some((x) => x.user_id === activeId) ? c : null
      ).catch(() => null)
    )).then((results) => {
      if (!cancelled) setStudentEnrollments({ uid: activeId, list: results.filter(Boolean) });
    });
    return () => { cancelled = true; };
  }, [activeId, classes]);

  const refreshEnrollments = async () => {
    if (!active) return;
    const enrolled = [];
    for (const c of classes) {
      const r = await api.get(`/classes/${c.class_id}/students`).catch(() => ({ data: [] }));
      if (r.data.some((s) => s.user_id === active.user_id)) enrolled.push(c);
    }
    setStudentEnrollments({ uid: active.user_id, list: enrolled });
  };

  const filtered = (students || []).filter((s) => {
    const q = search.trim().toLowerCase();
    return !q || (s.name || "").toLowerCase().includes(q) || (s.email || "").toLowerCase().includes(q);
  });

  const enroll = async (class_id) => {
    if (!active) return;
    try { await api.post("/org-admin/enrollments", { class_id, user_id: active.user_id }); toast.success("Assigned"); await refreshEnrollments(); }
    catch { toast.error("Couldn't assign"); }
  };
  const unenroll = async (class_id) => {
    if (!active) return;
    try { await api.delete("/org-admin/enrollments", { data: { class_id, user_id: active.user_id } }); toast.success("Removed"); await refreshEnrollments(); }
    catch { toast.error("Couldn't remove"); }
  };

  const submitImport = async (e) => {
    e.preventDefault();
    const file = e.target.elements.csv.files[0];
    if (!file) return toast.error("Pick a CSV file");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (importClass) fd.append("class_id", importClass);
      const r = await fetch(`${API}/org-admin/students/import`, { method: "POST", body: fd, credentials: "include" });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Failed");
      setImportResult(data);
      toast.success(`Imported · ${data.created} created, ${data.skipped_existing} existing, ${data.enrolled} enrolled${data.skipped_seat_limit ? `, ${data.skipped_seat_limit} skipped (seat limit)` : ""}`);
      load();
    } catch (e) { toast.error(e?.message || "Import failed"); }
    finally { setImporting(false); }
  };

  const overrideProgress = async () => {
    if (!active) return;
    if (!progressForm.surah || !progressForm.ayah) return toast.error("Surah & Ayah required");
    try {
      await api.post(`/org-admin/students/${active.user_id}/progress`, {
        surah: Number(progressForm.surah), ayah: Number(progressForm.ayah), status: progressForm.status,
      });
      toast.success("Progress set");
    } catch { toast.error("Couldn't set"); }
  };
  const resetProgress = async () => {
    if (!active) return;
    if (!window.confirm(`Reset ALL hifz progress for ${active.name}? This wipes their streak and XP too.`)) return;
    try { await api.post(`/org-admin/students/${active.user_id}/progress`, { reset_all: true }); toast.success("Progress reset"); load(); }
    catch { toast.error("Couldn't reset"); }
  };

  return (
    <div className="space-y-6" data-testid="org-students-tab">
      <details className="hifz-card" data-testid="org-bulk-import">
        <summary className="cursor-pointer font-display font-semibold text-forest-700 flex items-center gap-2">
          <Upload className="h-4 w-4" /> Bulk import students (CSV)
        </summary>
        <form onSubmit={submitImport} className="mt-4 space-y-3 text-sm">
          <p className="text-muted-foreground text-xs">CSV columns: <code>name, email, password</code> (password optional → defaults to <code>Welcome@2026</code>). Stops once your seat limit is reached. Pick a halaqa to auto-enroll everyone.</p>
          <input type="file" name="csv" accept=".csv" required className="block text-sm" data-testid="org-bulk-import-file" />
          <select value={importClass} onChange={(e) => setImportClass(e.target.value)} className="rounded-xl border border-sand-300 px-3 py-2 text-sm" data-testid="org-bulk-import-class">
            <option value="">— Don&apos;t auto-enroll —</option>
            {classes.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}</option>)}
          </select>
          <div>
            <button type="submit" disabled={importing} className="btn-primary text-sm" data-testid="org-bulk-import-submit">
              {importing ? "Importing…" : "Import CSV"}
            </button>
          </div>
          {importResult && (
            <div className="rounded-xl bg-forest-600/8 border border-forest-600/30 p-3 text-xs" data-testid="org-bulk-import-result">
              ✅ Created {importResult.created} · Skipped {importResult.skipped_existing} · Enrolled {importResult.enrolled}
              {importResult.skipped_seat_limit > 0 && <div className="mt-1 text-terracotta-600">{importResult.skipped_seat_limit} rows skipped — seat limit reached</div>}
              {importResult.errors?.length > 0 && <div className="mt-1 text-terracotta-600">{importResult.errors.length} row errors</div>}
            </div>
          )}
        </form>
      </details>

      <div className="grid gap-5">
        <aside className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input className="w-full rounded-xl border border-sand-300 bg-white pl-9 pr-3 py-2 text-sm" placeholder="Search students by name or email" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="org-students-search" />
          </div>
          {students === null ? <div className="text-muted-foreground text-sm">Loading…</div> : (
            <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
              {filtered.map((s) => (
                <li key={s.user_id}>
                  <button onClick={() => setActive(s)} data-testid={`org-student-row-${s.user_id}`}
                    className={`w-full text-left p-3 rounded-xl border ${active?.user_id === s.user_id ? "border-forest-600 bg-forest-600/8" : "border-sand-300 bg-white hover:bg-sand-50"}`}>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.email} · {s.total_memorized} ayahs · {s.streak}d</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section>
          {!active ? (
            <div className="hifz-card text-center py-12 text-muted-foreground" data-testid="org-students-pick">
              Pick a student to manage their halaqa assignments and progress.
            </div>
          ) : (
            <div className="hifz-card space-y-5" data-testid="org-student-detail">
              <header>
                <div className="font-display font-semibold text-xl text-forest-700">{active.name}</div>
                <div className="text-xs text-muted-foreground">{active.email}</div>
              </header>

              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">Halaqa assignments</div>
                {!studentEnrollments || studentEnrollments.uid !== active.user_id ? <div className="text-sm text-muted-foreground">Loading enrollments…</div> : (
                  <ul className="space-y-1.5 mb-3">
                    {studentEnrollments.list.length === 0 && <li className="text-sm text-muted-foreground">Not in any halaqa.</li>}
                    {studentEnrollments.list.map((c) => (
                      <li key={c.class_id} className="flex items-center justify-between bg-sand-200/40 border border-sand-300 rounded-xl px-3 py-2" data-testid={`org-enroll-row-${c.class_id}`}>
                        <span className="text-sm">{c.name}</span>
                        <button onClick={() => unenroll(c.class_id)} className="text-xs text-terracotta-600 hover:underline" data-testid={`org-unenroll-${c.class_id}`}>Remove</button>
                      </li>
                    ))}
                  </ul>
                )}
                <select className="rounded-xl border border-sand-300 px-3 py-2 text-sm w-full" onChange={(e) => { if (e.target.value) { enroll(e.target.value); e.target.value = ""; } }} data-testid="org-assign-class-select">
                  <option value="">+ Assign to halaqa…</option>
                  {classes.filter((c) => !(studentEnrollments?.uid === active.user_id ? studentEnrollments.list : []).find((x) => x.class_id === c.class_id)).map((c) => (
                    <option key={c.class_id} value={c.class_id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="rounded-2xl border border-sand-300 bg-sand-200/40 p-4 space-y-3" data-testid="org-progress-override">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold flex items-center gap-1.5">
                  <Move className="h-3 w-3" /> Override progress
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input className="rounded-xl border border-sand-300 px-2 py-1.5 text-sm" placeholder="Surah" type="number" value={progressForm.surah} onChange={(e) => setProgressForm({ ...progressForm, surah: e.target.value })} data-testid="org-prog-surah" />
                  <input className="rounded-xl border border-sand-300 px-2 py-1.5 text-sm" placeholder="Ayah" type="number" value={progressForm.ayah} onChange={(e) => setProgressForm({ ...progressForm, ayah: e.target.value })} data-testid="org-prog-ayah" />
                  <select className="rounded-xl border border-sand-300 px-2 py-1.5 text-sm" value={progressForm.status} onChange={(e) => setProgressForm({ ...progressForm, status: e.target.value })} data-testid="org-prog-status">
                    {["not_started", "learning", "memorized", "revising"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={overrideProgress} className="btn-accent text-xs" data-testid="org-prog-set">Set ayah</button>
                </div>
                <button onClick={resetProgress} className="text-xs text-terracotta-600 hover:underline" data-testid="org-prog-reset">Reset ALL progress (wipe streak + XP)</button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ============================================================== Announcements */
function AnnouncementsTab() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", audience: "all", expires_at: "" });
  const load = () => api.get("/org-admin/announcements").then((r) => setList(r.data)).catch(() => toast.error("Couldn't load"));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title & body required");
    try {
      const payload = { ...form };
      if (!payload.expires_at) delete payload.expires_at;
      else payload.expires_at = new Date(payload.expires_at).toISOString();
      await api.post("/org-admin/announcements", payload);
      toast.success("Announcement posted");
      setForm({ title: "", body: "", audience: "all", expires_at: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try { await api.delete(`/org-admin/announcements/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-4" data-testid="org-announcements-tab">
      <form onSubmit={create} className="hifz-card space-y-3" data-testid="org-announcement-form">
        <h3 className="font-display font-semibold text-forest-700">New announcement</h3>
        <input className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Title (max 120 chars)" maxLength={120} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="org-ann-title" />
        <textarea className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Body (max 600 chars)" maxLength={600} rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="org-ann-body" />
        <div className="grid gap-3">
          <select className="rounded-xl border border-sand-300 px-3 py-2 text-sm" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} data-testid="org-ann-audience">
            <option value="all">Everyone</option>
            <option value="students">Students only</option>
            <option value="teachers">Teachers only</option>
          </select>
          <input type="date" className="rounded-xl border border-sand-300 px-3 py-2 text-sm" placeholder="Expires" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="org-ann-expires" />
        </div>
        <button type="submit" className="btn-primary text-sm" data-testid="org-ann-submit"><Megaphone className="h-4 w-4" /> Post</button>
      </form>

      {list === null ? <div className="text-muted-foreground text-sm">Loading…</div> : list.length === 0 ? (
        <div className="hifz-card text-center py-12 text-muted-foreground">No announcements yet.</div>
      ) : (
        <ul className="space-y-2">
          {list.map((a) => (
            <li key={a.id} className="hifz-card flex items-start gap-4" data-testid={`org-ann-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-forest-700">{a.title}</div>
                <p className="text-sm text-foreground/80 mt-1">{a.body}</p>
                <div className="text-[11px] text-muted-foreground mt-1 capitalize">
                  Audience: {a.audience} · {new Date(a.created_at).toLocaleString()}
                  {a.expires_at && <> · Expires {new Date(a.expires_at).toLocaleDateString()}</>}
                </div>
              </div>
              <button onClick={() => del(a.id)} className="text-terracotta-600 p-2 hover:bg-terracotta-500/10 rounded-lg" data-testid={`org-ann-delete-${a.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================== Settings & Billing */
function SettingsTab() {
  const [org, setOrg] = useState(null);
  const load = () => api.get("/org-admin/settings").then((r) => setOrg(r.data)).catch(() => toast.error("Couldn't load organization settings"));
  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (!window.confirm("Rotate the institution code? The old code will stop working immediately.")) return;
    try { const r = await api.post("/org-admin/invite-code/rotate"); toast.success("Code rotated"); setOrg((o) => ({ ...o, org_code: r.data.org_code })); }
    catch { toast.error("Couldn't rotate code"); }
  };
  const copyCode = () => { navigator.clipboard.writeText(org.org_code); toast.success("Code copied"); };
  const copyLink = () => { navigator.clipboard.writeText(`${window.location.origin}/login?mode=register&org=${org.org_code}`); toast.success("Invite link copied"); };

  const toggleSharing = async (enabled) => {
    try {
      await api.post("/org-admin/settings/sharing", { public_sharing_enabled: enabled });
      setOrg((o) => ({ ...o, public_sharing_enabled: enabled }));
      toast.success(enabled ? "Your institution's activity can now appear publicly" : "Your institution's activity is private again");
    } catch { toast.error("Couldn't update sharing setting"); }
  };

  if (!org) return <div className="text-muted-foreground" data-testid="org-settings-loading">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="org-settings-tab">
      <div className="hifz-card space-y-3">
        <h3 className="font-display font-semibold text-forest-700 flex items-center gap-2"><KeyRound className="h-4 w-4" /> Institution code</h3>
        <p className="text-sm text-muted-foreground">Share this code (or the link) with students/teachers so they can join {org.name} when they register.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="px-3 py-2 rounded-xl bg-sand-200/60 border border-sand-300 text-sm font-semibold" data-testid="org-code-value">{org.org_code}</code>
          <button onClick={copyCode} className="btn-ghost text-xs" data-testid="org-code-copy"><Copy className="h-3.5 w-3.5" /> Copy code</button>
          <button onClick={copyLink} className="btn-ghost text-xs" data-testid="org-code-copy-link"><Copy className="h-3.5 w-3.5" /> Copy invite link</button>
          <button onClick={rotate} className="btn-ghost text-xs text-terracotta-600" data-testid="org-code-rotate"><RefreshCw className="h-3.5 w-3.5" /> Rotate code</button>
        </div>
      </div>

      <div className="hifz-card space-y-3">
        <h3 className="font-display font-semibold text-forest-700">Seats & plan</h3>
        <div className="grid gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Plan</div>
            <div className="mt-1 text-lg font-display font-semibold text-forest-700 capitalize">{org.plan_tier}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Seats used</div>
            <div className="mt-1 text-lg font-display font-semibold text-forest-700">{org.seats_used} / {org.seat_limit}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Billing status</div>
            <div className="mt-1 text-lg font-display font-semibold capitalize text-forest-700">{org.billing_status === "none" ? "No plan yet" : org.billing_status}</div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Billing management is coming soon — contact the platform administrator to change your seat limit or plan for now.</p>
      </div>

      <div className="hifz-card space-y-3">
        <h3 className="font-display font-semibold text-forest-700">Sharing outside your institution</h3>
        <p className="text-sm text-muted-foreground">
          By default, your students' milestones and leaderboard rankings are only visible to your own institution.
          Turn this on to let them also appear in the app-wide community feed and leaderboard.
        </p>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="org-sharing-toggle">
          <input type="checkbox" checked={!!org.public_sharing_enabled} onChange={(e) => toggleSharing(e.target.checked)} className="h-4 w-4" />
          <span className="text-sm font-medium">{org.public_sharing_enabled ? "Shared publicly" : "Private to your institution"}</span>
        </label>
      </div>
    </div>
  );
}

/* ============================================================== Main */
export default function OrgAdmin() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-8" data-testid="org-admin-panel">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">Organization admin</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">Manage your institution</h1>
        <p className="mt-2 text-foreground/70 max-w-2xl">
          Approve teachers, assign students to halaqas, broadcast announcements, and control what your institution shares outside itself.
        </p>
      </header>

      <div className="flex flex-wrap gap-1.5 border-b border-sand-300 pb-2" data-testid="org-admin-tabs">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`org-admin-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-t-xl text-sm font-medium transition ${
              tab === t.id ? "bg-forest-600 text-sand-100" : "text-foreground/70 hover:bg-sand-200"
            }`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === "overview" && <OverviewTab />}
        {tab === "teachers" && <TeachersTab />}
        {tab === "students" && <StudentsTab />}
        {tab === "halaqas" && <HalaqasTab />}
        {tab === "announcements" && <AnnouncementsTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
